import { cn } from './internal/utils'
import { BORDER, RADIUS } from './internal/presets'
import Empty from './Empty'
import Spin from './Spin'
import Pagination from './Pagination'

const Meta = ({ avatar, title, description, className, ...rest }) => (
  <div className={cn('ui-list-item-meta flex min-w-0 flex-1 items-start gap-3', className)} {...rest}>
    {avatar ? <div className="ui-list-item-meta-avatar shrink-0">{avatar}</div> : null}
    <div className="ui-list-item-meta-content min-w-0 flex-1">
      {title ? (
        <div className="ui-list-item-meta-title font-medium text-[var(--text-dark)]">{title}</div>
      ) : null}
      {description ? (
        <div className="ui-list-item-meta-description text-sm text-[var(--text-muted)]">
          {description}
        </div>
      ) : null}
    </div>
  </div>
)

const Item = ({ actions, extra, className, children, ...rest }) => (
  <li
    className={cn(
      'ui-list-item flex items-center gap-3 border-b px-3 py-2.5 last:border-b-0',
      BORDER,
      className
    )}
    {...rest}
  >
    <div className="flex min-w-0 flex-1 items-center gap-3">{children}</div>
    {actions?.length ? (
      <ul className="ui-list-item-action flex shrink-0 items-center gap-3 p-0">
        {actions.map((action, index) => (
          // eslint-disable-next-line react/no-array-index-key -- positional
          <li key={index}>{action}</li>
        ))}
      </ul>
    ) : null}
    {extra ? <div className="ui-list-item-extra shrink-0">{extra}</div> : null}
  </li>
)

Item.Meta = Meta

/**
 * antd List. Supports dataSource + renderItem, header/footer, bordered,
 * size, loading, locale.emptyText, grid and pagination.
 */
const List = ({
  dataSource = [],
  renderItem,
  rowKey,
  header,
  footer,
  bordered = false,
  size = 'default',
  split = true,
  loading = false,
  locale,
  grid,
  pagination,
  itemLayout,
  className,
  style,
  children,
  ...rest
}) => {
  const resolveKey = (item, index) => {
    if (typeof rowKey === 'function') return rowKey(item)
    if (rowKey && item?.[rowKey] != null) return item[rowKey]
    return item?.key ?? item?.id ?? index
  }

  const paginated = pagination
    ? dataSource.slice(
        ((pagination.current || 1) - 1) * (pagination.pageSize || 10),
        (pagination.current || 1) * (pagination.pageSize || 10)
      )
    : dataSource

  const items = renderItem
    ? paginated.map((item, index) => (
        <div key={resolveKey(item, index)} className={grid ? 'ui-list-grid-item' : undefined}>
          {renderItem(item, index)}
        </div>
      ))
    : children

  const isEmpty = renderItem && paginated.length === 0

  return (
    <div
      className={cn(
        'ui-list',
        bordered && `ui-list-bordered border ${BORDER} ${RADIUS}`,
        size === 'small' && 'ui-list-sm text-sm',
        !split && 'ui-list-split-none [&_.ui-list-item]:border-b-0',
        className
      )}
      style={style}
      {...rest}
    >
      {header ? (
        <div className={cn('ui-list-header border-b px-3 py-2', BORDER)}>{header}</div>
      ) : null}

      <Spin spinning={loading}>
        {isEmpty ? (
          <Empty description={locale?.emptyText ?? 'Aucune donnée'} />
        ) : (
          <ul
            className={cn(
              'ui-list-items m-0 list-none p-0',
              grid && 'grid gap-3',
              grid?.gutter != null && ''
            )}
            style={
              grid
                ? {
                    gridTemplateColumns: `repeat(${grid.column || 4}, minmax(0, 1fr))`,
                    gap: grid.gutter ?? 12,
                  }
                : undefined
            }
          >
            {items}
          </ul>
        )}
      </Spin>

      {footer ? (
        <div className={cn('ui-list-footer border-t px-3 py-2', BORDER)}>{footer}</div>
      ) : null}

      {pagination ? (
        <div className="ui-list-pagination flex justify-end px-3 py-2">
          <Pagination total={dataSource.length} {...pagination} />
        </div>
      ) : null}
    </div>
  )
}

List.Item = Item

export default List
